import { useState, useEffect } from 'react'
import { Save, RefreshCw, Globe, Bug, Smartphone, Activity, Megaphone, Download } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import ImagePicker from '../ondemand-library/content-catalog/ImagePicker'

const MOBILE_KEYS = ['splash_mobile_image_1', 'splash_mobile_image_2', 'splash_mobile_image_3']

// Pull a clean embed string out of the /sponsors/zones/:id/adtag response —
// mirrors the extraction in sponsor-manager/zones/ZoneDetail.jsx.
function extractServingCode(adtag) {
  if (!adtag) return ''
  if (typeof adtag === 'string') return adtag
  if (typeof adtag.ServingCode === 'string') return adtag.ServingCode
  if (typeof adtag.Zone?.ServingCode === 'string') return adtag.Zone.ServingCode
  if (adtag.Zone?.ServingCode?.['#text']) return adtag.Zone.ServingCode['#text']
  return adtag.AdTag || adtag.Tag || ''
}

// Strip any refresh=NN param — AdBanner.jsx owns refresh via the Page
// Visibility API; a server-side refresh would cause runaway ad-server hits.
function stripRefresh(tag) {
  return (tag || '').replace(/([?&;])refresh=\d+/gi, '').trim()
}

// AdSpeed's ServingCode emits http:// URLs. The watch site is HTTPS, so an
// http script tag is blocked as mixed content (blank banner). Upgrade to https.
function upgradeToHttps(tag) {
  return (tag || '').replace(/http:\/\//gi, 'https://')
}

const DEFAULTS = {
  splash_hero_image: 'https://imagedelivery.net/W52fz0ACsU_Tb7DooNcIsA/96691c8e-7c5e-4120-0db2-8dbdbef9f700/public',
  splash_hero_title: 'Community Sponsored Streaming TV',
  splash_hero_description: "Stream Greater Pittsburgh's best local sports, music, arts, community events, podcasts and more — LIVE and On-Demand! and ALWAYS FREE, 24/7 on TenbandTV!",
  splash_tag_line: 'Sponsored Locally, Streaming Freely',
  splash_preview_title_live: 'Upcoming Live',
  splash_preview_title_vod: 'Recent On Demand',
  splash_preview_title_highlights: 'Recent Highlights',
  splash_mobile_image_1: '',
  splash_mobile_image_2: '',
  splash_mobile_image_3: '',
  store_url_web: '/watch',
  store_url_mobile: '/watch',
  store_url_roku: 'https://channelstore.roku.com/details/daf775d2a84a31b02d42d80208f4b0e2:60655fe70eba5e2846e9227c1b3e55bf/tenbandtv',
  store_url_firetv: 'https://www.amazon.com/gp/product/B0FD3KNLZF',
  log_level: 'warn',
  mux_enabled: 'true',
  mux_debug: 'false',
  mux_settle_ms: '10000',
  ads_global_enabled: 'true',
  ads_live_banner_enabled: 'true',
  ads_live_banner_mode: 'manual',
  ads_live_banner_zone_id: '',
  ads_live_banner_zone_name: '',
  ads_live_banner_tag: '',
  ads_live_banner_width: '970',
  ads_live_banner_height: '250',
  ads_live_banner_frequency: '3',
  // (Hero rail ads 300×600 moved to Player Settings — see PlayerSettings.jsx.)
  ads_ondemand_row_frequency: '0',
  ads_splash_live_frequency: '3',
  ads_splash_vod_frequency: '3',
  ads_splash_highlights_frequency: '3',
}

const LOG_LEVELS = [
  { value: 'off',   label: 'Off',   desc: 'No console logging' },
  { value: 'error', label: 'Error', desc: 'Errors only' },
  { value: 'warn',  label: 'Warn',  desc: 'Warnings and errors (production default)' },
  { value: 'info',  label: 'Info',  desc: 'Informational messages, warnings, and errors' },
  { value: 'debug', label: 'Debug', desc: 'Everything including detailed player/stream diagnostics' },
]

export default function SiteSettings() {
  const { api } = useAuth()

  const [settings, setSettings] = useState({ ...DEFAULTS })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')
  const [mobileTab, setMobileTab] = useState(0)

  // Web-ads zone picker
  const [zones, setZones] = useState([])
  const [zonesLoading, setZonesLoading] = useState(false)
  const [resolvingTag, setResolvingTag] = useState(false)

  useEffect(() => { loadSettings() }, [])

  async function loadSettings() {
    setLoading(true)
    try {
      const [splashData, storeData, logData, muxData, adsData] = await Promise.all([
        api('/settings?prefix=splash_'),
        api('/settings?prefix=store_'),
        api('/settings?prefix=log_'),
        api('/settings?prefix=mux_'),
        api('/settings?prefix=ads_'),
      ])
      const s = {
        ...(splashData.settings || {}),
        ...(storeData.settings || {}),
        ...(logData.settings || {}),
        ...(muxData.settings || {}),
        ...(adsData.settings || {}),
      }
      setSettings(prev => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(s).filter(([, v]) => v !== null && v !== '')
        ),
      }))
    } catch (e) {
      setStatus(`Error loading settings: ${e.message}`)
    }
    setLoading(false)
  }

  async function handleSave() {
    setSaving(true)
    setStatus('')
    try {
      // The hero rail (ads_live_rail_*) moved to Player Settings — exclude those keys here so Site Settings
      // never round-trips/clobbers them (PUT upserts per key).
      const payload = Object.fromEntries(Object.entries(settings).filter(([k]) => !k.startsWith('ads_live_rail_')))
      await api('/settings', {
        method: 'PUT',
        body: JSON.stringify({ settings: payload })
      })
      setStatus('Settings saved. Changes will appear on watch.tenband.tv within 5 minutes (cache TTL).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  function handleChange(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  function handleReset() {
    setSettings({ ...DEFAULTS })
    setStatus('')
  }

  // Lazy-load the sponsor zone list the first time the user switches the
  // live banner to "select a zone" mode.
  useEffect(() => {
    const wantZones = settings.ads_live_banner_mode === 'zone'
    if (!wantZones || zones.length || zonesLoading) return
    setZonesLoading(true)
    api('/sponsors/zones?status=active')
      .then(d => setZones(d.zones || []))
      .catch(e => setStatus(`Error loading zones: ${e.message}`))
      .finally(() => setZonesLoading(false))
  }, [settings.ads_live_banner_mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve a zone's JavaScript ServingCode into the stored tag. AdBanner only
  // renders JS tags, so we always request format=javascript regardless of how
  // the zone is configured upstream.
  async function resolveZoneTag(zoneId, zoneName, prefix = 'ads_live_banner') {
    if (!zoneId) return
    setResolvingTag(true)
    setStatus('')
    try {
      const d = await api(`/sponsors/zones/${zoneId}/adtag?format=javascript`)
      if (d.ok === false) throw new Error(d.error?.message || 'AdSpeed returned no tag')
      const tag = upgradeToHttps(stripRefresh(extractServingCode(d.adtag)))
      if (!tag) throw new Error('Zone returned an empty tag')
      setSettings(prev => ({
        ...prev,
        [`${prefix}_zone_id`]: String(zoneId),
        [`${prefix}_zone_name`]: zoneName || prev[`${prefix}_zone_name`] || '',
        [`${prefix}_tag`]: tag,
      }))
      setStatus('Zone tag fetched. Review it below, then Save.')
    } catch (e) {
      setStatus(`Error fetching zone tag: ${e.message}`)
    }
    setResolvingTag(false)
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 700 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 4px' }}>Site Settings</h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading...</p>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: '0 0 4px' }}>
          <Globe size={20} style={{ display: 'inline', marginRight: 8, verticalAlign: 'text-bottom' }} />
          Site Settings
        </h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 13, margin: 0 }}>
          Splash page content for watch.tenband.tv — hero image, tagline, and section labels
        </p>
      </div>

      {status && (
        <div style={{
          padding: '8px 14px', borderRadius: 6, fontSize: 13, marginBottom: 16,
          background: status.startsWith('Error') ? 'rgba(233,69,96,0.1)' : 'rgba(76,175,80,0.1)',
          color: status.startsWith('Error') ? '#e94560' : '#4caf50',
          border: `1px solid ${status.startsWith('Error') ? 'rgba(233,69,96,0.2)' : 'rgba(76,175,80,0.2)'}`,
        }}>
          {status}
        </div>
      )}

      {/* Hero Section */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>Splash Hero</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          The main hero section on the splash/home page
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Hero Image URL</label>
          <input
            type="text"
            value={settings.splash_hero_image}
            onChange={e => handleChange('splash_hero_image', e.target.value)}
            placeholder={DEFAULTS.splash_hero_image}
            style={{
              width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
          {settings.splash_hero_image && (
            <div style={{ marginTop: 10, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--color-border)', maxWidth: 300 }}>
              <img
                src={settings.splash_hero_image}
                alt="Hero preview"
                style={{ width: '100%', height: 'auto', display: 'block' }}
                onError={e => { e.target.style.display = 'none' }}
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Hero Title</label>
          <input
            type="text"
            value={settings.splash_hero_title}
            onChange={e => handleChange('splash_hero_title', e.target.value)}
            placeholder={DEFAULTS.splash_hero_title}
            style={{
              width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
          <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0' }}>Hidden on mobile, visible on tablet and up</p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Hero Description</label>
          <textarea
            value={settings.splash_hero_description}
            onChange={e => handleChange('splash_hero_description', e.target.value)}
            placeholder={DEFAULTS.splash_hero_description}
            rows={3}
            style={{
              width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
              resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          />
          <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0' }}>
            Use LIVE, On-Demand, and ALWAYS FREE — these keywords are automatically highlighted in the splash page
          </p>
        </div>

        <div style={{ marginBottom: 0 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Tag Line</label>
          <input
            type="text"
            value={settings.splash_tag_line}
            onChange={e => handleChange('splash_tag_line', e.target.value)}
            placeholder={DEFAULTS.splash_tag_line}
            style={{
              width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
          <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0' }}>
            Short slogan exposed via the watch API for use on mobile apps and other surfaces
          </p>
        </div>
      </div>

      {/* Mobile Splash Images */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>
          <Smartphone size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
          Mobile Splash Images
        </h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          Up to 3 images shown on the mobile splash page. One is chosen at random per visit. Empty slots are skipped.
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {MOBILE_KEYS.map((key, i) => {
            const active = mobileTab === i
            const hasImage = !!settings[key]
            return (
              <button
                key={key}
                type="button"
                onClick={() => setMobileTab(i)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: active ? 'rgba(59,130,246,0.15)' : 'var(--color-bg)',
                  color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
                  border: `1px solid ${active ? 'rgba(59,130,246,0.4)' : 'var(--color-border)'}`,
                }}
              >
                Image {i + 1}
                <span style={{
                  display: 'inline-block', marginLeft: 8, width: 8, height: 8, borderRadius: '50%',
                  background: hasImage ? '#4caf50' : 'transparent',
                  border: hasImage ? 'none' : '1px solid var(--color-border)',
                  verticalAlign: 'middle',
                }} />
              </button>
            )
          })}
        </div>

        <ImagePicker
          value={settings[MOBILE_KEYS[mobileTab]] || ''}
          onChange={v => handleChange(MOBILE_KEYS[mobileTab], v)}
          placeholder="https://imagedelivery.net/..."
        />
      </div>

      {/* Download / Store Links */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>
          <Download size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
          Download / Store Links
        </h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          The device tiles in the "How To Watch" section on the splash and about pages.
          Changes apply on watch.tenband.tv within 5 minutes (cache TTL) — no redeploy needed.
        </p>

        {[
          { key: 'store_url_web', label: 'Web', hint: 'Usually the internal watch path (/watch) or a full URL' },
          { key: 'store_url_mobile', label: 'Mobile', hint: 'App Store / Google Play link once the mobile apps launch (placeholder: /watch)' },
          { key: 'store_url_roku', label: 'Roku', hint: 'Roku Channel Store listing URL' },
          { key: 'store_url_firetv', label: 'Amazon Fire TV', hint: 'Amazon Appstore product URL' },
        ].map(({ key, label, hint }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</label>
            <input
              type="text"
              value={settings[key]}
              onChange={e => handleChange(key, e.target.value)}
              placeholder={DEFAULTS[key]}
              style={{
                width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
            <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0' }}>{hint}</p>
          </div>
        ))}
      </div>

      {/* Preview Section Labels */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>Preview Section</h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          Tab labels for the content preview section below the hero
        </p>

        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Live Tab Label</label>
            <input
              type="text"
              value={settings.splash_preview_title_live}
              onChange={e => handleChange('splash_preview_title_live', e.target.value)}
              placeholder={DEFAULTS.splash_preview_title_live}
              style={{
                width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>VOD Tab Label</label>
            <input
              type="text"
              value={settings.splash_preview_title_vod}
              onChange={e => handleChange('splash_preview_title_vod', e.target.value)}
              placeholder={DEFAULTS.splash_preview_title_vod}
              style={{
                width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Highlights Tab Label</label>
            <input
              type="text"
              value={settings.splash_preview_title_highlights}
              onChange={e => handleChange('splash_preview_title_highlights', e.target.value)}
              placeholder={DEFAULTS.splash_preview_title_highlights}
              style={{
                width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      </div>

      {/* Mux Data */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>
          <Activity size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
          Mux Data (Video Analytics)
        </h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          Controls Mux Data video-performance tracking on watch.tenband.tv and other watch apps.
          Changes apply within 5 minutes (cache TTL).
        </p>

        {/* Enabled toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 12 }}>
          <input
            type="checkbox"
            checked={settings.mux_enabled === 'true'}
            onChange={e => handleChange('mux_enabled', e.target.checked ? 'true' : 'false')}
            style={{ margin: 0, width: 16, height: 16 }}
          />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Enabled</span>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Master on/off — when off, no analytics are collected (no Mux Data cost)</span>
        </label>

        {/* Debug logging toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={settings.mux_debug === 'true'}
            onChange={e => handleChange('mux_debug', e.target.checked ? 'true' : 'false')}
            style={{ margin: 0, width: 16, height: 16 }}
          />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Debug logging</span>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Verbose Mux console logs (also enableable locally via the MUX log category)</span>
        </label>

        {/* Dwell threshold */}
        <div style={{ maxWidth: 340 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Count a view after the viewer settles (seconds)</label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={Number(settings.mux_settle_ms || 10000) / 1000}
            onChange={e => {
              const secs = parseFloat(e.target.value)
              handleChange('mux_settle_ms', String(Number.isFinite(secs) ? Math.max(0, Math.round(secs * 1000)) : 10000))
            }}
            style={{
              width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
              border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
          <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '6px 0 0', lineHeight: 1.4 }}>
            Debounces channel-surfing and preview-browsing — a channel or video only counts as a view once it has played continuously this long (previews and full watches alike; pausing resets it). Default 10s.
          </p>
        </div>
      </div>

      {/* Web Ads */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>
          <Megaphone size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
          Web Ads
        </h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          Controls the 970×250 banner shown on LIVE and Splash (between the Upcoming Live and Recent On-Demand cards)
          across watch.tenband.tv and other watch apps. Changes apply within 5 minutes (cache TTL).
        </p>

        {/* Global kill switch */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={settings.ads_global_enabled === 'true'}
            onChange={e => handleChange('ads_global_enabled', e.target.checked ? 'true' : 'false')}
            style={{ margin: 0, width: 16, height: 16 }}
          />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Ads enabled</span>
          <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Master on/off — when off, no ad zones render on any page</span>
        </label>

        {/* Live banner sub-card */}
        <div style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6, padding: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
            <input
              type="checkbox"
              checked={settings.ads_live_banner_enabled === 'true'}
              onChange={e => handleChange('ads_live_banner_enabled', e.target.checked ? 'true' : 'false')}
              style={{ margin: 0, width: 16, height: 16 }}
            />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Live banner zone</span>
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>The 970×250 banner on LIVE/Splash</span>
          </label>

          {/* Source mode */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Ad source</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { value: 'manual', label: 'Enter URL / tag manually' },
                { value: 'zone', label: 'Select a sponsor zone' },
              ].map(opt => {
                const active = (settings.ads_live_banner_mode || 'manual') === opt.value
                return (
                  <label
                    key={opt.value}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                      padding: '8px 12px', borderRadius: 6, fontSize: 13,
                      background: active ? 'rgba(59,130,246,0.1)' : 'transparent',
                      border: `1px solid ${active ? 'rgba(59,130,246,0.3)' : 'var(--color-border)'}`,
                    }}
                  >
                    <input
                      type="radio"
                      name="ads_live_banner_mode"
                      value={opt.value}
                      checked={active}
                      onChange={e => handleChange('ads_live_banner_mode', e.target.value)}
                      style={{ margin: 0 }}
                    />
                    {opt.label}
                  </label>
                )
              })}
            </div>
          </div>

          {/* Zone picker (mode = zone) */}
          {settings.ads_live_banner_mode === 'zone' && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Sponsor zone</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={settings.ads_live_banner_zone_id || ''}
                  disabled={zonesLoading}
                  onChange={e => {
                    const id = e.target.value
                    const z = zones.find(z => String(z.id ?? z.ZoneId) === id)
                    handleChange('ads_live_banner_zone_id', id)
                    handleChange('ads_live_banner_zone_name', z ? String(z.name ?? z.Name ?? '') : '')
                  }}
                  style={{
                    flex: 1, background: 'var(--color-bg)', color: 'var(--color-text)',
                    border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13,
                  }}
                >
                  <option value="">{zonesLoading ? 'Loading zones…' : '— Select a zone —'}</option>
                  {zones.map(z => {
                    const id = String(z.id ?? z.ZoneId)
                    return <option key={id} value={id}>{(z.name ?? z.Name ?? `Zone ${id}`)} (#{id})</option>
                  })}
                </select>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={!settings.ads_live_banner_zone_id || resolvingTag}
                  onClick={() => resolveZoneTag(settings.ads_live_banner_zone_id, settings.ads_live_banner_zone_name)}
                >
                  {resolvingTag ? 'Fetching…' : 'Fetch tag'}
                </button>
              </div>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '6px 0 0', lineHeight: 1.4 }}>
                Fetches the zone's JavaScript ad tag. AdBanner renders JS tags only — VAST/IFrame zone formats are not supported here.
              </p>
            </div>
          )}

          {/* Tag — editable in manual mode, read-only preview in zone mode */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              {settings.ads_live_banner_mode === 'zone' ? 'Resolved ad tag' : 'Ad tag / URL'}
            </label>
            <textarea
              value={settings.ads_live_banner_tag}
              readOnly={settings.ads_live_banner_mode === 'zone'}
              onChange={e => handleChange('ads_live_banner_tag', e.target.value)}
              rows={3}
              placeholder={'<script type="text/javascript" src="https://ads.tenband.tv/ad.php?do=js&zid=…&oid=28740&wd=-1&ht=-1&target=_blank"></script>'}
              style={{
                width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
                border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 12,
                fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box',
                opacity: settings.ads_live_banner_mode === 'zone' ? 0.8 : 1,
              }}
            />
            <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0', lineHeight: 1.4 }}>
              Empty tag → watch falls back to its built-in default. Do not add a refresh param — refresh is handled client-side.
            </p>
          </div>

          {/* Size + frequency */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Width</label>
              <input
                type="number" min="1"
                value={settings.ads_live_banner_width}
                onChange={e => handleChange('ads_live_banner_width', e.target.value)}
                style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Height</label>
              <input
                type="number" min="1"
                value={settings.ads_live_banner_height}
                onChange={e => handleChange('ads_live_banner_height', e.target.value)}
                style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Cards between ads</label>
              <input
                type="number" min="0"
                value={settings.ads_live_banner_frequency}
                onChange={e => handleChange('ads_live_banner_frequency', e.target.value)}
                style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
              />
              <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0' }}>Insert after every Nth card on the <strong>LIVE page</strong> / Preview / LIVE2. <strong>0 = no ads.</strong> (Splash tabs use the per-tab values below.)</p>
            </div>
          </div>

          {/* OnDemand row frequency */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Rows between ads (OnDemand)</label>
            <input
              type="number" min="0"
              value={settings.ads_ondemand_row_frequency}
              onChange={e => handleChange('ads_ondemand_row_frequency', e.target.value)}
              style={{ maxWidth: 160, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
            />
            <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0', lineHeight: 1.4 }}>
              Insert the banner after every Nth content row on the OnDemand page (uses the same ad tag above). <strong>0 = no ads between rows.</strong>
            </p>
          </div>

          {/* Splash tab ad frequency (per-tab, cards between ads) */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Splash tab ad frequency</label>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 11, margin: '0 0 10px', lineHeight: 1.4 }}>
              Cards between ads for each tab on the Splash (home) page, independently. <strong>0 = no ads on that tab.</strong>
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Live tab</label>
                <input
                  type="number" min="0"
                  value={settings.ads_splash_live_frequency}
                  onChange={e => handleChange('ads_splash_live_frequency', e.target.value)}
                  style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>On-Demand tab</label>
                <input
                  type="number" min="0"
                  value={settings.ads_splash_vod_frequency}
                  onChange={e => handleChange('ads_splash_vod_frequency', e.target.value)}
                  style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Highlights tab</label>
                <input
                  type="number" min="0"
                  value={settings.ads_splash_highlights_frequency}
                  onChange={e => handleChange('ads_splash_highlights_frequency', e.target.value)}
                  style={{ width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Logging */}
      <div style={{ background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>
          <Bug size={16} style={{ display: 'inline', marginRight: 6, verticalAlign: 'text-bottom' }} />
          Client Logging
        </h3>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
          Controls console log verbosity on watch.tenband.tv for all viewers.
          Users can override locally via <code style={{ fontSize: 11, background: 'var(--color-bg)', padding: '1px 4px', borderRadius: 3 }}>localStorage.setItem('tbtv_log_level', 'debug')</code>
        </p>

        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Log Level</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {LOG_LEVELS.map(lvl => (
              <label
                key={lvl.value}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  padding: '6px 10px', borderRadius: 6,
                  background: settings.log_level === lvl.value ? 'rgba(59,130,246,0.1)' : 'transparent',
                  border: `1px solid ${settings.log_level === lvl.value ? 'rgba(59,130,246,0.3)' : 'transparent'}`,
                }}
              >
                <input
                  type="radio"
                  name="log_level"
                  value={lvl.value}
                  checked={settings.log_level === lvl.value}
                  onChange={e => handleChange('log_level', e.target.value)}
                  style={{ margin: 0 }}
                />
                <span style={{ fontSize: 13, fontWeight: 600, minWidth: 50 }}>{lvl.label}</span>
                <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{lvl.desc}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn-ghost" onClick={handleReset} disabled={saving}>
          <RefreshCw size={14} /> Reset to Defaults
        </button>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  )
}
